import { coerceBooleanProperty, coerceNumberProperty } from '@angular/cdk/coercion';
import { NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    contentChild,
    effect,
    inject,
    input,
    NgZone,
    output,
    signal,
    TemplateRef,
    viewChild,
    viewChildren,
    ViewEncapsulation,
} from '@angular/core';
import { Cluster, GoogleMap, MapAdvancedMarker, MapInfoWindow, MapMarkerClusterer } from '@angular/google-maps';
import { MatIcon } from '@angular/material/icon';
import { EnpsComponent } from '@common/components/base/enps-component';
import { FooterDirective } from '@common/directives/footer-directive';
import { ItemDirective } from '@common/directives/item-directive';
import { Store } from '@ngrx/store';
import { NomWebServicesJson } from '@src/suite/common/services/nom-web-services-json';
import { selectAllRecords } from '@src/suite/common/store/playbook/playbook.selector';

const DEFAULT_HEIGHT = '87vh';
const DEFAULT_WIDTH = null;

const MAP_DARK_MODE_STYLES = [
    { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
    {
        featureType: 'administrative.locality',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#d59563' }],
    },
    {
        featureType: 'poi',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#d59563' }],
    },
    {
        featureType: 'poi.park',
        elementType: 'geometry',
        stylers: [{ color: '#263c3f' }],
    },
    {
        featureType: 'poi.park',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#6b9a76' }],
    },
    {
        featureType: 'road',
        elementType: 'geometry',
        stylers: [{ color: '#38414e' }],
    },
    {
        featureType: 'road',
        elementType: 'geometry.stroke',
        stylers: [{ color: '#212a37' }],
    },
    {
        featureType: 'road',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#9ca5b3' }],
    },
    {
        featureType: 'road.highway',
        elementType: 'geometry',
        stylers: [{ color: '#746855' }],
    },
    {
        featureType: 'road.highway',
        elementType: 'geometry.stroke',
        stylers: [{ color: '#1f2835' }],
    },
    {
        featureType: 'road.highway',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#f3d19c' }],
    },
    {
        featureType: 'transit',
        elementType: 'geometry',
        stylers: [{ color: '#2f3948' }],
    },
    {
        featureType: 'transit.station',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#d59563' }],
    },
    {
        featureType: 'water',
        elementType: 'geometry',
        stylers: [{ color: '#17263c' }],
    },
    {
        featureType: 'water',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#515c6d' }],
    },
    {
        featureType: 'water',
        elementType: 'labels.text.stroke',
        stylers: [{ color: '#17263c' }],
    },
];
@Component({
    selector: 'st-map-view',
    imports: [GoogleMap, MapAdvancedMarker, MapInfoWindow, MapMarkerClusterer, MatIcon, NgTemplateOutlet],
    templateUrl: './map-view.html',
    styleUrl: './map-view.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-map-view',
    },
})
export class MapViewComponent extends EnpsComponent {
    private ngZone = inject(NgZone);
    private changeDetectorRef = inject(ChangeDetectorRef);
    private nomWebServicesJson = inject(NomWebServicesJson);

    store = inject(Store);
    readonly mapLockLocation = input(false, {
        transform: coerceBooleanProperty,
    });

    readonly mapZoomToCoverage = input(true, {
        transform: coerceBooleanProperty,
    });

    readonly mapZoom = input(10, {
        transform: val => coerceNumberProperty(val, 10),
    });

    readonly mapCurrentCenter = input('');
    readonly currentClusterMaxZoom = input(10, {
        transform: val => coerceNumberProperty(val, 10),
    });

    readonly markerData = input([]);

    readonly width = input(DEFAULT_WIDTH);
    readonly height = input(DEFAULT_HEIGHT);

    readonly mapZoomChange = output<number>();
    readonly mapCurrentCenterChange = output<string>();
    readonly mapCurrentBoundsChange = output<string>();

    readonly openInfoWindow = output<any>();

    readonly infoWindow = viewChild(MapInfoWindow);
    readonly map = viewChild(GoogleMap);

    readonly mapMarkers = viewChildren(MapAdvancedMarker);

    readonly footerTemplate = contentChild(FooterDirective, { read: TemplateRef });
    readonly itemTemplate = contentChild(ItemDirective, { read: TemplateRef });

    readonly mapReady = signal(false);
    readonly currentOpenedMarker = signal<StorytellingMarker>(null);
    readonly currentMapBounds = signal<google.maps.LatLngBounds>(null);

    readonly options = signal<google.maps.MapOptions>({
        center: new google.maps.LatLng(40, -20),
        disableDefaultUI: true,
        zoomControl: false,
        zoom: 10,
        fullscreenControl: false,
        mapTypeControl: false,
        streetViewControl: false,
        keyboardShortcuts: false,
        gestureHandling: 'greedy',
        styles: this.nomWebServicesJson.HelloResults.DarkMode ? MAP_DARK_MODE_STYLES : null,
    });

    readonly currentOpenedItems = computed(() => {
        if (this.currentOpenedMarker()) {
            const records = this.store.selectSignal(selectAllRecords)();
            return this.currentOpenedMarker()
                .guids.map(guid => {
                    return records.get(guid) || null;
                })
                .filter(val => val);
        }
        return [];
    });

    // left for reference if we want to use that marker image later
    // readonly markerImgPath = 'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m';

    constructor() {
        super();

        effect(() => {
            this.calculateControls(this.mapLockLocation());
        });
    }

    zoomIn() {
        if (this.mapLockLocation()) return;
        this.mapZoomChange.emit(this.map().getZoom() + 1);
    }

    zoomOut() {
        if (this.mapLockLocation()) return;
        this.mapZoomChange.emit(this.map().getZoom() - 1);
    }

    mapInit() {
        this.mapReady.set(true);
        this.calculateControls(this.mapLockLocation());
        this.init();
    }

    updateCurrentCenter() {
        // update current bounds
        if (this.map() && this.mapReady()) {
            const latLng = this.map().getCenter();
            const c = `${latLng.lat()}\t${latLng.lng()}\t${new Date()}\t${null}\n`;
            if (this.mapCurrentCenter() !== c) {
                this.currentMapBounds.set(this.map().getBounds());
                this.mapCurrentCenterChange.emit(c);
            }
        }
    }

    updateCurrentZoom() {
        // update current bounds
        if (this.map() && this.mapReady()) {
            const zoom = this.map().getZoom();
            if (this.mapZoom() !== zoom) {
                this.currentMapBounds.set(this.map().getBounds());
                this.mapZoomChange.emit(zoom);
            }
        }
    }

    init(fitDefaultBounds?: boolean) {
        if (this.mapZoomToCoverage() && !fitDefaultBounds) {
            this.fitMapBounds();
        } else {
            this.fitDefaultBounds();
        }
    }

    private fitMapBounds() {
        const bounds: google.maps.LatLngBounds = new google.maps.LatLngBounds();
        let hasBounds = false;
        for (const marker of this.markerData()) {
            bounds.extend(new google.maps.LatLng(marker.latitude, marker.longitude));
            hasBounds = true;
        }
        if (this.map()) {
            if (this.map().fitBounds) {
                if (hasBounds) {
                    this.map().fitBounds(bounds);
                } else {
                    // ask user
                    this.fitDefaultBounds();
                    //TODO left this code because I am not sure if we need to use defauld bounds(if we use default bounds works like in 9.5.1)
                    // const geoError = () => {};
                    // const geoSuccess = position => {
                    //     bounds.extend(new google.maps.LatLng(position.coords.latitude, position.coords.longitude));
                    //     this.mapZoomChange.emit(17);
                    //     this.map().fitBounds(bounds);
                    // };
                    // navigator.geolocation.getCurrentPosition(geoSuccess, geoError);
                }
            }
        }
    }

    private calculateControls(mapLockLocation: boolean) {
        if (this.map() && this.mapReady()) {
            this.options.update(opts => {
                return {
                    ...opts,
                    panControl: mapLockLocation,
                    gestureHandling: mapLockLocation ? 'none' : 'auto',
                    center: this.map().getCenter(),
                };
            });
        }
    }

    fitDefaultBounds() {
        let lat = 0;
        let lng = 0;

        if (this.mapCurrentCenter() && this.mapCurrentCenter().length > 0) {
            const parts = this.mapCurrentCenter().split('\t');
            if (parts.length >= 2) {
                lat = parseFloat(parts[0]);
                lng = parseFloat(parts[1]);

                if (this.map() && typeof lat !== 'undefined' && typeof lng !== 'undefined') {
                    this.map().center = new google.maps.LatLng(lat, lng);
                }
            }
        }
    }

    markerClick(marker: any) {
        const currentMarker = this.mapMarkers().find(m => m.advancedMarker.position.lat === marker.latitude && m.advancedMarker.position.lng === marker.longitude);
        this.currentOpenedMarker.set(marker);
        this.infoWindow().open(currentMarker);
    }

    zoomToCoverage() {
        this.ngZone.runOutsideAngular(() => {
            setTimeout(
                () => {
                    this.init();
                },
                1,
                this.changeDetectorRef
            );
        });
    }

    updateCurrentBounds() {
        this.mapCurrentBoundsChange.emit(this.map().getBounds().toUrlValue());
        this.map().getBounds();
    }
    markerClustererClicked(cluster: Cluster) {
        if (this.mapLockLocation()) {
            this.markerClick(cluster.markers[0]);
        } else {
            // TODO: handle the max zoom level for clusters
            this.map().fitBounds(cluster.bounds);
        }
    }
}
